import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ClinqLogo } from "../components/ClinqLogo";
import { CheckCircle2, Clock, FileText, TrendingUp, Users, Sparkles, ArrowRight } from "lucide-react";

const TIERS = [
  {
    name: "Tier 1", price: "KES 10,000", hours: "Up to 4 hours",
    features: [
      "Up to 4 hours of analyst support per month",
      "Document preparation and platform upload",
      "CRS score guidance",
      "Virtual or in-person sessions (Nairobi only, transport charged separately)",
    ],
  },
  {
    name: "Tier 2", price: "KES 20,000", hours: "Up to 10 hours", popular: true,
    features: [
      "Up to 10 hours of analyst support per month",
      "Everything in Tier 1",
    ],
  },
  {
    name: "Tier 3", price: "KES 30,000", hours: "Up to 20 hours",
    features: [
      "Up to 20 hours of analyst support per month",
      "Everything in Tier 2",
      "Monthly review call with a senior Clinq expert to personally oversee your capital readiness process",
    ],
  },
];

export default function AnalystSupport() {
  const [form, setForm] = useState({ full_name: "", company_name: "", email: "", mobile: "", tier: "Not sure yet" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/public/analyst-support-leads", form);
      setDone(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F8F8]" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Header */}
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5">
        <Link to="/" data-testid="analyst-logo-link"><ClinqLogo size="md" tone="teal" /></Link>
        <Link to="/" className="text-sm font-bold text-[#0A8B85] hover:underline" data-testid="analyst-header-platform-link">
          Go to the platform →
        </Link>
      </header>

      {/* Hero */}
      <section className="bg-[#06302E] text-white">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:py-24">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#2FBBB3]/40 bg-[#0A8B85]/20 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#7BD3CE]"
             data-testid="analyst-hero-badge">
            <Sparkles className="h-3.5 w-3.5" /> Clinq Analyst Support
          </p>
          <h1 className="mt-6 max-w-3xl text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl"
              data-testid="analyst-hero-headline">
            Too busy to capital raise?<br />
            <span className="text-[#2FBBB3]">We've got you.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B9D9D6] md:text-lg" data-testid="analyst-hero-copy">
            You are building a business. Between operations, your team, and your clients, capital
            raising keeps getting pushed to the bottom of the list. Clinq Analyst Support pairs you
            with a dedicated financial analyst who does the heavy lifting for you — pulling together
            your documentation, uploading it to the platform, and guiding you through every step of
            your capital readiness journey.
          </p>
          <p className="mt-4 text-lg font-bold text-white">
            You focus on your business. We get you funder-ready.
          </p>
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-[#B9D9D6]">
            <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#2FBBB3]" /> Documents done for you</span>
            <span className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#2FBBB3]" /> CRS score guidance</span>
            <span className="flex items-center gap-2"><Users className="h-4 w-4 text-[#2FBBB3]" /> A dedicated analyst</span>
          </div>
          <a href="#interest" data-testid="analyst-hero-cta"
             className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#0A8B85] px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#0C9F97]">
            Tell us you're interested <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* Tiers */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:py-20">
        <h2 className="text-base font-bold uppercase tracking-widest text-[#0A8B85] md:text-lg">Choose your level of support</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {TIERS.map((t) => (
            <div key={t.name}
              data-testid={`analyst-tier-${t.name.replace(" ", "-").toLowerCase()}`}
              className={"relative flex flex-col rounded-2xl border bg-white p-7 shadow-sm transition-transform hover:-translate-y-1 " +
                (t.popular ? "border-[#0A8B85] ring-2 ring-[#0A8B85]/20" : "border-neutral-200")}>
              {t.popular && (
                <span className="absolute -top-3 left-6 rounded-full bg-[#0A8B85] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                  Most popular
                </span>
              )}
              <p className="text-sm font-bold uppercase tracking-widest text-neutral-500">{t.name}</p>
              <p className="mt-3 text-3xl font-extrabold text-[#06302E]">{t.price}
                <span className="text-sm font-semibold text-neutral-500"> / month</span>
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-[#0A8B85]">
                <Clock className="h-4 w-4" /> {t.hours} of analyst support
              </p>
              <ul className="mt-5 space-y-3 text-sm text-neutral-700">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0A8B85]" /> {f}
                  </li>
                ))}
              </ul>
              <a href="#interest"
                 className={"mt-auto pt-7 text-sm font-bold " + (t.popular ? "text-[#0A8B85]" : "text-neutral-600 hover:text-[#0A8B85]")}>
                I'm interested in {t.name} →
              </a>
            </div>
          ))}
        </div>
        <p className="mt-8 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-center text-sm font-semibold text-amber-800"
           data-testid="analyst-topup-note">
          Need more hours? Top up at KES 1,500 per hour on any plan.
        </p>
      </section>

      {/* Interest form */}
      <section id="interest" className="mx-auto max-w-2xl px-5 pb-20">
        <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm">
          {done ? (
            <div className="py-10 text-center" data-testid="analyst-form-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-[#0A8B85]" />
              <p className="mt-4 text-xl font-bold text-[#06302E]">Thank you!</p>
              <p className="mt-2 text-sm text-neutral-600">
                A member of the Clinq team will be in touch with you shortly.
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-extrabold text-[#06302E]">Get a dedicated analyst in your corner</h2>
              <p className="mt-2 text-sm text-neutral-600">
                Tell us a little about you — we'll reach out to match you with the right analyst.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-sm font-bold text-neutral-800">Full Name <span className="text-red-500">*</span></label>
                  <input required value={form.full_name} onChange={set("full_name")}
                    data-testid="analyst-form-name"
                    className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85]" />
                </div>
                <div>
                  <label className="text-sm font-bold text-neutral-800">Company Name <span className="text-red-500">*</span></label>
                  <input required value={form.company_name} onChange={set("company_name")}
                    data-testid="analyst-form-company"
                    className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85]" />
                </div>
                <div>
                  <label className="text-sm font-bold text-neutral-800">Email Address <span className="text-red-500">*</span></label>
                  <input type="email" required value={form.email} onChange={set("email")}
                    data-testid="analyst-form-email"
                    className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85]" />
                </div>
                <div>
                  <label className="text-sm font-bold text-neutral-800">Mobile Number <span className="text-neutral-400">(optional)</span></label>
                  <input value={form.mobile} onChange={set("mobile")} placeholder="+254 ..."
                    data-testid="analyst-form-mobile"
                    className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85]" />
                </div>
                <div>
                  <label className="text-sm font-bold text-neutral-800">Which tier are you interested in?</label>
                  <select value={form.tier} onChange={set("tier")}
                    data-testid="analyst-form-tier"
                    className="mt-1 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85]">
                    <option>Tier 1</option>
                    <option>Tier 2</option>
                    <option>Tier 3</option>
                    <option>Not sure yet</option>
                  </select>
                </div>
                {err && <p className="text-sm font-semibold text-red-500" data-testid="analyst-form-error">{String(err)}</p>}
                <button type="submit" disabled={busy} data-testid="analyst-form-submit"
                  className="w-full rounded-full bg-[#0A8B85] py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#0C9F97] disabled:opacity-50">
                  {busy ? "Sending…" : "I am interested — get in touch"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-8">
          <ClinqLogo size="sm" tone="teal" testid="analyst-footer-logo" />
          <Link to="/" className="text-sm font-bold text-[#0A8B85] hover:underline" data-testid="analyst-footer-platform-link">
            Back to the Clinq platform →
          </Link>
        </div>
      </footer>
    </div>
  );
}
